/**
 * Cookie auth adapter for web runtime.
 *
 * Implements CookieAuthAdapter by wrapping the existing AuthApiClient.
 * Converts between API types (SessionData) and shared types (FrontendUser).
 */

import { Result } from "true-myth/result";

import type { SessionData, WebUser } from "@animus/api-treaty";
import type {
  AuthError,
  CookieAuthAdapter,
  FrontendUser,
} from "@animus/frontend-features/auth-provider-cookie";
import { createLocalStorageCache } from "@animus/frontend-features/auth-store";
import { parseRole } from "@animus/frontend-features/rbac";

import { AuthApi } from "./auth-api-client";

// ─────────────────────────────────────────────────────────────────────────────
// Auth Cache
// ─────────────────────────────────────────────────────────────────────────────

const AUTH_CACHE_KEY = "animus:auth:user";

/**
 * Web auth cache.
 *
 * Shared between:
 * - AuthCookieProvider (for optimistic rendering)
 * - Route guards (for sync auth checks in beforeLoad)
 */
export const webAuthCache = createLocalStorageCache(AUTH_CACHE_KEY);

// ─────────────────────────────────────────────────────────────────────────────
// Converters
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Convert API WebUser to FrontendUser.
 *
 * WebUser has `role: string | null` and `isAnonymous: boolean | null`.
 * FrontendUser has `role: Role` (typed enum) and `isAnonymous: boolean`.
 */
function toFrontendUser(user: WebUser): FrontendUser {
  return {
    id: user.id,
    email: user.email,
    name: user.name,
    role: parseRole(user.role),
    image: user.image,
    isAnonymous: user.isAnonymous ?? false,
    emailVerified: user.emailVerified,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Adapter Implementation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Web cookie auth adapter.
 *
 * Uses Better Auth cookie-based authentication via the API client.
 * Caching is handled separately via AuthCache - see app.tsx.
 */
export const webCookieAdapter: CookieAuthAdapter = {
  async getSession(): Promise<
    Result<{ user: FrontendUser } | null, AuthError>
  > {
    try {
      const session: SessionData | null = await AuthApi.getSession();
      if (!session) {
        return Result.ok(null);
      }
      return Result.ok({ user: toFrontendUser(session.user) });
    } catch (error) {
      return Result.err({
        type: "network",
        message: error instanceof Error ? error.message : "Network error",
        canRetry: true,
      });
    }
  },

  async signIn(
    email: string,
    password: string
  ): Promise<Result<{ user: FrontendUser }, AuthError>> {
    const result = await AuthApi.signIn(email, password);
    return result.mapOrElse(
      (error) => Result.err(error),
      (session) => Result.ok({ user: toFrontendUser(session.user) })
    );
  },

  async signUp(
    email: string,
    password: string,
    name: string
  ): Promise<Result<{ user: FrontendUser }, AuthError>> {
    const result = await AuthApi.signUp(email, password, name);
    return result.mapOrElse(
      (error) => Result.err(error),
      (session) => Result.ok({ user: toFrontendUser(session.user) })
    );
  },

  async signInAnonymous(): Promise<Result<{ user: FrontendUser }, AuthError>> {
    const result = await AuthApi.signInAnonymous();
    return result.mapOrElse(
      (error) => Result.err(error),
      (session) => Result.ok({ user: toFrontendUser(session.user) })
    );
  },

  async signOut(): Promise<Result<void, AuthError>> {
    try {
      await AuthApi.signOut();
      return Result.ok(undefined);
    } catch (error) {
      return Result.err({
        type: "network",
        message: error instanceof Error ? error.message : "Network error",
        canRetry: true,
      });
    }
  },
};
